"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTocOptionsState = exports.TocOptionsStateProvider = exports.TocOptionsState = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const ui_1 = require("@jsonjoy.com/ui");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const settings = tslib_1.__importStar(require("./settings"));
const getElementSignature = (element) => JSON.stringify([
    element.caption ?? '',
    element.maxLevel ?? null,
    element.includeTitle ?? null,
    element.numbered ?? null,
]);
const TocOptionsStateContext = React.createContext(null);
class TocOptionsState {
    constructor(editor, element, closePopup) {
        this.editor = editor;
        this.caption = ui_1.rsync.val('');
        this.maxLevel = ui_1.rsync.val(settings.DEF_MAX_LEVEL);
        this.includeTitle = ui_1.rsync.val(settings.DEF_INCLUDE_TITLE);
        this.numbered = ui_1.rsync.val(settings.DEF_NUMBERED);
        this.signature = '';
        this.setElement = (element) => {
            const signature = getElementSignature(element);
            this.element = element;
            if (signature === this.signature)
                return;
            this.syncFromElement(element, signature);
        };
        this.setClosePopup = (closePopup) => {
            this.closePopup = closePopup;
        };
        this.setCaption = (value) => {
            this.caption.set(value);
        };
        this.setMaxLevel = (value) => {
            this.maxLevel.set(settings.getTocMaxLevel(value));
        };
        this.setIncludeTitle = (value) => {
            this.includeTitle.set(!!value);
        };
        this.setNumbered = (value) => {
            this.numbered.set(!!value);
        };
        this.cancel = () => {
            this.syncFromElement(this.element);
            this.closePopup?.();
        };
        this.apply = () => {
            const nextCaption = this.caption.value.trim();
            const nextMaxLevel = settings.getStoredTocMaxLevel(this.maxLevel.value);
            const nextIncludeTitle = settings.getStoredTocIncludeTitle(this.includeTitle.value);
            const nextNumbered = settings.getStoredTocNumbered(this.numbered.value);
            this.applyString('caption', nextCaption, this.element.caption);
            this.applyValue('maxLevel', nextMaxLevel, this.element.maxLevel);
            this.applyValue('includeTitle', nextIncludeTitle, this.element.includeTitle);
            this.applyValue('numbered', nextNumbered, this.element.numbered);
            this.caption.set(nextCaption);
            this.closePopup?.();
        };
        this.dispose = () => { };
        this.syncFromElement = (element, signature = getElementSignature(element)) => {
            this.signature = signature;
            this.caption.set(element.caption ?? '');
            this.maxLevel.set(settings.getTocMaxLevel(element.maxLevel));
            this.includeTitle.set(settings.getTocIncludeTitle(element.includeTitle));
            this.numbered.set(settings.getTocNumbered(element.numbered));
        };
        this.applyString = (field, value, currentValue) => {
            const prev = (currentValue ?? '').trim();
            if (value === prev)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.applyValue = (field, value, currentValue) => {
            if (value === currentValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value !== undefined) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.element = element;
        this.closePopup = closePopup;
        this.syncFromElement(element);
    }
}
exports.TocOptionsState = TocOptionsState;
const TocOptionsStateProvider = ({ editor, element, closePopup, children, }) => {
    const stateRef = React.useRef(null);
    if (!stateRef.current)
        stateRef.current = new TocOptionsState(editor, element, closePopup);
    const state = stateRef.current;
    React.useEffect(() => {
        state.setClosePopup(closePopup);
    }, [closePopup, state]);
    React.useEffect(() => {
        state.setElement(element);
    }, [element, state]);
    React.useEffect(() => {
        return () => state.dispose();
    }, [state]);
    return React.createElement(TocOptionsStateContext.Provider, { value: state }, children);
};
exports.TocOptionsStateProvider = TocOptionsStateProvider;
const useTocOptionsState = () => {
    const state = React.useContext(TocOptionsStateContext);
    if (!state)
        throw new Error('TocOptionsStateContext is not available.');
    return state;
};
exports.useTocOptionsState = useTocOptionsState;
